<template>
  <component :is="wrapper" :class="['input', {'input-expand': width === 'expand'}]">
    <label :for="id" v-if="label">{{ label }}</label>
    <input
      :id="id"
      :disabled="disabled"
      :type="type"
      :value="value"
      :class="state"
      :placeholder="placeholder"
      ref="inp"
		v-bind="$attrs"
		v-bind:value="value"
		v-on="inputListeners"
      @input="onInput($event.target.value)"
      @focus="onFocus($event.target.value)"
    >
  </component>
</template>

<script>
/**
 * Form Inputs are used to allow users to provide text input when the expected
 * input is short. Form Input has a range of options and supports several text
 * formats including numbers. For longer input, use the form `Textarea` element.
 */
export default {
  name: "Input",
  status: "ready",
  release: "1.0.0",
  inheritAttrs: false,
  props: {
    /**
     * The type of the form input field.
     * `text, number, email`
     */
    type: {
      type: String,
      default: "text",
      validator: value => {
        return value.match(/(text|number|email|password)/)
      },
    },
    /**
     * Text value of the form input field.
     */
    value: {
      type: String,
      default: null,
    },
    /**
     * The placeholder value for the form input field.
     */
    placeholder: {
      type: String,
      default: null,
    },
    /**
     * The label of the form input field.
     */
    label: {
      type: String,
      default: null,
    },
    /**
     * The html element name used for the wrapper.
     * `div, section`
     */
    wrapper: {
      type: String,
      default: "div",
      validator: value => {
        return value.match(/(div|section)/)
      },
    },
    /**
     * Unique identifier of the form input field.
     */
    id: {
      type: String,
      default: null,
    },
/*
    vref: {
      type: String,
      default: null,
    },
*/
    /**
     * The width of the form input field.
     * `auto, expand`
     */
    width: {
      type: String,
      default: "expand",
      validator: value => {
        return value.match(/(auto|expand)/)
      },
    },
    /**
     * Whether the form input field is disabled or not.
     * `true, false`
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Manually trigger various states of the input.
     * `hover, active, focus`
     */
    state: {
      type: String,
      default: null,
      validator: value => {
        return value.match(/(hover|active|focus)/)
      },
    },
  },
  computed: {
    inputListeners: function () {
      var vm = this
      // `Object.assign` merges objects together to form a new object
      return Object.assign({},
        // We add all the listeners from the parent
        this.$listeners,
        // Then we can add custom listeners or override the
        // behavior of some listeners.
        {
          // This ensures that the component works with v-model
          input: function (event) {
            vm.$emit('input', event.target.value)
          }
        }
      )
    }
  },
  methods: {
    onInput(value) {
      this.$emit("change", value)
    },
    onFocus(value) {
      this.$emit("focus", value)
    },
  },
}
</script>

<style scoped>
.input {
  width: auto;
}

.input-expand {
	width: 100%;
}

.input input {
    transition: all 0.2s ease;
    -webkit-appearance: none;
    appearance: none;
    background: #fff;
    color: #333;
    width: 100%;
    margin: 0;
    border: 0;
    box-shadow: inset 0 1px 0 0 rgba(0, 0, 0, 0.07),
      0 0 0 1px tint(0, 0, 0, 80%);
	border: 1px solid #888;
	line-height: 2;
	padding:2px 5px 2px 5px;
}

.input input::-webkit-input-placeholder {
  -webkit-font-smoothing: antialiased;
  color: #888;
}
.input input:-ms-input-placeholder {
  color: #888;
}
.input input::-moz-placeholder {
  color: #888;
  -moz-osx-font-smoothing: grayscale;
  opacity: 1;
}
.input input[disabled] {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  box-shadow: 0 0 0 1px tint(0, 0, 0, 80%);
  background: lighten(#888, 42%);
  color: tint(#888, 20%);
  cursor: not-allowed;
  opacity: 0.7;
}

.input label {
	cursor: pointer;
	display: block;
	color: #000;
}

</style>
